import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { Textarea } from '#/modules/commonComponents/form/Textarea';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { LINE_REMARK_TEXTAREA_CLASS } from '#/modules/imsMgmt/receipts/shared/lineRemarkTextareaClass';
import {
  mapInvStockRowToAdjustmentLine,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/formSerialize';
import {
  ADJUSTMENT_LINES_MERGE_TIP,
} from '#/modules/imsMgmt/receipts/stockAdjustment/schemas/stockAdjustmentSchema';
import InvStockPickerDialog from '#/modules/imsMgmt/receipts/stockTransfer/components/InvStockPickerDialog';
import { formatDisplayQty } from '#/utils/formatNumber';

/* eslint-disable max-len -- Tailwind arbitrary grid-cols must stay as one static class string */
const ADJUSTMENT_LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(0,7.5rem)_minmax(10rem,1fr)_minmax(0,9rem)_minmax(0,5.5rem)_minmax(8rem,10rem)_minmax(0,5.5rem)_minmax(8rem,1fr)_2.5rem]';
/* eslint-enable max-len */

const ADJUSTMENT_LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[60rem]',
);

const LINE_HEADER_CLASS = cn(
  'hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  ADJUSTMENT_LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-3 px-3 py-3',
  '@3xl/form-section:items-center',
  ADJUSTMENT_LINE_GRID_COLS,
);

const FIELD_CELL_CLASS = 'min-w-0';

/** 手機欄位小標；mb-1 與輸入框留距（≥@3xl 隱藏） */
const MOBILE_FIELD_LABEL_CLASS = cn(
  'mb-1 text-xs text-gray-500',
  '@3xl/form-section:hidden',
);

function previewAdjustedQty(availableQty, baseQty) {
  if (baseQty === '' || baseQty == null) {
    return null;
  }
  const delta = Number(baseQty);
  if (!Number.isFinite(delta)) {
    return null;
  }
  return Number(availableQty) + delta;
}

const LineRemoveButton = ({ index, onRemove, ariaLabel }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon-sm"
    className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
    aria-label={ariaLabel}
    onClick={() => onRemove(index)}
  >
    <PiTrashBold className="size-4" />
  </Button>
);

LineRemoveButton.propTypes = {
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

const AdjustmentLineRow = ({
  index,
  register,
  errors,
  line,
  onRemove,
}) => {
  const availableQty = Number(line.availableQty) || 0;
  const previewQty = previewAdjustedQty(availableQty, line.baseQty);
  const warehouseLabel = line.warehouseLabel || '-';
  const remarkId = `lines.${index}.remark`;
  const remarkError = getNestedError(errors, remarkId);
  const removeAriaLabel = `移除明細 ${index + 1}`;

  return (
    <li className={LINE_ROW_CLASS}>
      <div className="flex min-w-0 items-center justify-between gap-2 @3xl/form-section:hidden">
        <p className="text-sm font-medium tabular-nums text-gray-700">
          {`項目# ${index + 1}.`}
        </p>
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>庫存編號</p>
        <p className="font-mono text-sm text-gray-800">{line.stockNo || '-'}</p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>物料</p>
        <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>倉別／儲位</p>
        <p className="truncate text-sm text-gray-800" title={warehouseLabel}>
          {warehouseLabel}
        </p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>目前量</p>
        <p className="text-sm tabular-nums text-gray-700">
          {formatDisplayQty(availableQty)}
        </p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <PlainRegisteredInput
          id={`lines.${index}.baseQty`}
          type="number"
          step="1"
          register={register}
          errors={errors}
          placeholder="調整量"
          mobileLabel="調整量"
          suppressInlineError
        />
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>調整後</p>
        <p
          className={cn(
            'text-sm tabular-nums',
            previewQty != null && previewQty < 0 ? 'text-red-500' : 'text-gray-700',
          )}
        >
          {previewQty == null ? '—' : formatDisplayQty(previewQty)}
        </p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>行備註</p>
        <Textarea
          id={remarkId}
          rows={3}
          aria-label={`行備註 ${index + 1}`}
          aria-invalid={remarkError ? true : undefined}
          title={remarkError?.message || undefined}
          className={LINE_REMARK_TEXTAREA_CLASS}
          {...register(remarkId)}
        />
      </div>
      <div
        className="
          hidden min-w-0
          @3xl/form-section:flex @3xl/form-section:justify-end
        "
      >
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
    </li>
  );
};

AdjustmentLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const StockAdjustmentLinesEditor = ({
  control,
  register,
  errors,
  getValues,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const { fields, remove, replace } = useFieldArray({
    control,
    name: 'lines',
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });

  const linesForRender = useMemo(() => (
    (fields ?? []).map((field, index) => {
      const watched = watchedLines?.[index] ?? {};
      return {
        ...field,
        ...watched,
        id: field.id,
      };
    })
  ), [fields, watchedLines]);

  const initialSelectedRows = useMemo(
    () => linesForRender
      .filter((line) => line.invStockId)
      .map((line) => ({
        id: line.invStockId,
        stockNo: line.stockNo,
        mtr: {
          mtrNo: line.mtrNo,
          name: line.mtrName,
        },
        baseQty: line.availableQty,
      })),
    [linesForRender],
  );

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const existingById = new Map(
      (getValues('lines') || [])
        .filter((line) => line.invStockId)
        .map((line) => [line.invStockId, line]),
    );

    const nextLines = (selectedRows || [])
      .map((row) => {
        const id = row?.id;
        if (!id) {
          return null;
        }
        const kept = existingById.get(id);
        if (kept) {
          return kept;
        }
        return mapInvStockRowToAdjustmentLine(row);
      })
      .filter(Boolean);

    replace(nextLines);
  }

  return (
    <section className="@container/form-section">
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-2">
        <h2 className="form-block__title mb-0! mr-2">調整明細</h2>
        <Button
          type="button"
          size="sm"
          onClick={handleOpenPicker}
        >
          選擇庫存
        </Button>
      </div>

      <p className="mb-3 text-xs text-gray-500">{ADJUSTMENT_LINES_MERGE_TIP}</p>

      {errors?.lines?.message ? (
        <p className="mb-3 text-sm text-red-500">{errors.lines.message}</p>
      ) : null}

      <div className="overflow-x-auto">
        {linesForRender.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-gray-400">
            尚未選擇任何庫存
          </p>
        ) : (
          <div className={ADJUSTMENT_LINES_MIN_W}>
            <div className={LINE_HEADER_CLASS} aria-hidden>
              <span className="min-w-0">庫存編號</span>
              <span className="min-w-0">物料</span>
              <span className="min-w-0">倉／儲位</span>
              <span className="min-w-0">目前量</span>
              <span className="min-w-0">調整量</span>
              <span className="min-w-0">調整後</span>
              <span className="min-w-0">行備註</span>
              <span className="sr-only min-w-0">操作</span>
            </div>
            <ul className="m-0 list-none divide-y divide-gray-200 p-0">
              {linesForRender.map((line, index) => (
                <AdjustmentLineRow
                  key={line.id}
                  index={index}
                  register={register}
                  errors={errors}
                  line={line}
                  onRemove={remove}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      <InvStockPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
        title="請選擇欲調整的庫存"
        description="多選後確認；同單不可重複同一庫存。"
      />
    </section>
  );
};

StockAdjustmentLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  getValues: PropTypes.func.isRequired,
};

export default StockAdjustmentLinesEditor;
